import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContractorAuthStore } from '../../lib/contractorAuth';
import type { ContractorAuthResponse } from '../../lib/contractorAuth';
import { contractorApi } from '../../lib/contractorApi';
import { API_BASE } from '../../lib/apiBase';

const TRADES = [
  { value: 'hvac',                    label: 'HVAC' },
  { value: 'plumbing',                label: 'Plumbing' },
  { value: 'electrical',              label: 'Electrical' },
  { value: 'roofing',                 label: 'Roofing' },
  { value: 'general',                 label: 'General / Handyman' },
  { value: 'pest_control',            label: 'Pest Control' },
  { value: 'landscaping',             label: 'Landscaping' },
  { value: 'painting',                label: 'Painting' },
  { value: 'appliance_repair',        label: 'Appliance Repair' },
  { value: 'flooring',                label: 'Flooring' },
  { value: 'garage_door_technician',  label: 'Garage Door' },
  { value: 'deck_fence_contractor',   label: 'Deck & Fence' },
  { value: 'waterproofing_contractor', label: 'Waterproofing' },
  { value: 'window_contractor',       label: 'Windows' },
  { value: 'concrete_contractor',     label: 'Concrete / Driveways' },
  { value: 'dryer_vent_specialist',   label: 'Dryer Vent Cleaning' },
  { value: 'insulation_contractor',   label: 'Insulation' },
] as const;

const formSchema = z.object({
  business_name:    z.string().min(2).max(200).trim(),
  owner_name:       z.string().min(2).max(100).trim(),
  email:            z.string().email().max(320),
  password:         z.string().min(8).max(200),
  confirm_password: z.string().min(1, 'Please confirm your password'),
  phone:            z.string().min(7).max(20).trim(),
  primary_trade:    z.string().min(1, 'Select a trade'),
  license_state:    z.string().length(2, 'Enter 2-letter state code').toUpperCase(),
  license_number:   z.string().max(50).trim().optional(),
  zip_codes:        z.string().min(5, 'Enter at least one ZIP code'),
  tier_requested:   z.enum(['self_service', 'verified_partner']),
  early_access_ack: z.literal(true, {
    errorMap: () => ({ message: 'Please acknowledge the Early Access notice to continue.' }),
  }),
  terms_accepted: z.boolean().refine((v) => v === true, {
    message: 'Please agree to the Privacy Policy and Terms of Service to continue.',
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type FormValues = z.infer<typeof formSchema>;
type Plan = 'free' | 'pro' | 'enterprise';

function errMsg(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e) return String((e as { message: string }).message);
  return 'Registration failed. Please try again.';
}

const input = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white';

const PLAN_LABEL: Record<Plan, string> = {
  free:       'Free Listing',
  pro:        'Pro — $49/mo',
  enterprise: 'Enterprise — $149/mo',
};

export function ContractorSignup() {
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const setAuth      = useContractorAuthStore((s) => s.setAuth);

  const planParam = params.get('plan') as Plan | null;
  const isPaidPlan = planParam === 'pro' || planParam === 'enterprise';

  const [step, setStep]   = useState<'tier' | 'form'>(() => planParam ? 'form' : 'tier');
  const [error, setError] = useState('');

  const {
    register, handleSubmit, watch, setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tier_requested: isPaidPlan ? 'verified_partner' : 'self_service',
    },
  });

  // Sync plan param to form value when navigating here from marketing site
  useEffect(() => {
    if (isPaidPlan) {
      setValue('tier_requested', 'verified_partner');
    }
  }, [isPaidPlan, setValue]);

  const selectedTier  = watch('tier_requested');
  const termsAccepted = watch('terms_accepted');

  async function onSubmit(data: FormValues) {
    setError('');
    const zipCodes = data.zip_codes
      .split(/[\s,]+/)
      .map(z => z.trim())
      .filter(z => /^\d{5}$/.test(z));

    if (zipCodes.length === 0) {
      setError('Enter at least one valid 5-digit ZIP code.');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/contractor/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name:     data.business_name,
          owner_name:        data.owner_name,
          email:             data.email.toLowerCase(),
          password:          data.password,
          phone:             data.phone,
          primary_trade:     data.primary_trade,
          license_state:     data.license_state,
          license_number:    data.license_number ?? '',
          service_zip_codes: zipCodes,
          tier_requested:    data.tier_requested,
          early_access_ack:  data.early_access_ack,
          terms_accepted:    data.terms_accepted,
        }),
      });

      if (!res.ok) {
        const body = await res.json() as { error?: { message: string } };
        setError(body.error?.message ?? 'Registration failed.');
        return;
      }

      const body = await res.json() as ContractorAuthResponse;
      setAuth(body.contractor, body.access_token, body.expires_in);

      // If a paid plan was requested, redirect to Stripe Checkout
      if (isPaidPlan) {
        const checkout = await contractorApi.post<{ url: string }>(
          '/contractor/billing/subscription',
          { plan: planParam },
        );
        window.location.href = checkout.url;
        return;
      }

      navigate('/verify-pending', { replace: true });
    } catch (e) {
      setError(errMsg(e));
    }
  }

  if (step === 'tier') {
    return (
      <div className="min-h-dvh bg-gray-50 flex flex-col">
        <div className="bg-blue-700 pt-14 pb-8 px-4">
          <h1 className="text-white text-2xl font-bold">List Your Business</h1>
          <p className="text-white/75 text-sm mt-1">Choose how you want to appear on Housewizrd</p>
        </div>

        <div className="px-4 pt-6 space-y-4 flex-1">
          <button
            onClick={() => { setValue('tier_requested', 'self_service'); setStep('form'); }}
            className="w-full bg-white rounded-2xl shadow-sm p-5 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-black text-gray-900">Free Listing</span>
                  <span className="text-xs bg-green-100 text-green-800 font-bold px-2 py-0.5 rounded-full">Instant</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  List your business for free. Pay only for leads you accept — no subscription required.
                </p>
                <div className="mt-3 space-y-1.5">
                  {[
                    'Active immediately — no review needed',
                    'Listed in the Housewizrd directory',
                    'Pay per lead ($5–15 per lead accepted)',
                    'No monthly fees',
                  ].map(f => (
                    <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-blue-600">✓</span>{f}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-gray-400 mt-1">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => { setValue('tier_requested', 'verified_partner'); setStep('form'); }}
            className="w-full bg-gradient-to-b from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-5 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-black text-blue-900">Verified Partner</span>
                  <span className="text-xs bg-blue-700 text-white font-bold px-2 py-0.5 rounded-full">$49/mo</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Get the Housewizrd Verified badge. Exclusive leads delivered to you before anyone else sees them.
                </p>
                <div className="mt-3 space-y-1.5">
                  {[
                    'Housewizrd Verified badge — higher homeowner trust',
                    'Exclusive first-access leads in your ZIP codes',
                    'Background check + license verification included',
                    'Priority ranking in search results',
                  ].map(f => (
                    <div key={f} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-blue-600">✓</span>{f}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-blue-400 mt-1">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            </div>
          </button>

          <p className="text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/contractor/login" className="text-blue-700 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-gray-50 pb-10">
      <div className={`pt-14 pb-6 px-4 ${selectedTier === 'verified_partner' ? 'bg-gradient-to-br from-blue-800 to-blue-900' : 'bg-blue-700'}`}>
        {planParam ? (
          <Link to="/" className="flex items-center gap-1 text-white/80 text-sm font-medium mb-4">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </Link>
        ) : (
          <button
            onClick={() => setStep('tier')}
            className="flex items-center gap-1 text-white/80 text-sm font-medium mb-4"
            style={{ minHeight: 44 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
        )}

        <h1 className="text-white text-xl font-bold">
          {planParam ? PLAN_LABEL[planParam] : selectedTier === 'verified_partner' ? 'Verified Partner Application' : 'Create Free Listing'}
        </h1>
        {(isPaidPlan || selectedTier === 'verified_partner') && (
          <p className="text-white/70 text-sm mt-1">
            {isPaidPlan
              ? `You'll be redirected to billing after signup to start your ${planParam} subscription.`
              : "You'll be contacted within 2 business days to complete verification."}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="px-4 pt-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Business Info</p>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Business Name</label>
            <input {...register('business_name')} placeholder="Acme HVAC Services" className={input} />
            {errors.business_name && <p className="text-xs text-red-500 mt-1">{errors.business_name.message}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Owner Name</label>
            <input {...register('owner_name')} placeholder="John Smith" className={input} />
            {errors.owner_name && <p className="text-xs text-red-500 mt-1">{errors.owner_name.message}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Primary Trade</label>
            <select {...register('primary_trade')} className={input}>
              <option value="">Select your trade…</option>
              {TRADES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {errors.primary_trade && <p className="text-xs text-red-500 mt-1">{errors.primary_trade.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">License State</label>
              <input {...register('license_state')} placeholder="GA" maxLength={2} className={input} style={{ textTransform: 'uppercase' }} />
              {errors.license_state && <p className="text-xs text-red-500 mt-1">{errors.license_state.message}</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">License # (optional)</label>
              <input {...register('license_number')} placeholder="LIC-12345" className={input} />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Service ZIP Codes</label>
            <textarea
              {...register('zip_codes')}
              placeholder="31553, 31554, 31557, 31558…"
              rows={3}
              className={`${input} resize-none`}
            />
            <p className="text-xs text-gray-400 mt-1">Separate with commas or spaces</p>
            {errors.zip_codes && <p className="text-xs text-red-500 mt-1">{errors.zip_codes.message}</p>}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Account</p>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Email</label>
            <input {...register('email')} type="email" placeholder="you@company.com" className={input} />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Phone</label>
            <input {...register('phone')} type="tel" placeholder="(555) 000-0000" className={input} />
            {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Password</label>
            <input {...register('password')} type="password" autoComplete="new-password" placeholder="8+ characters" className={input} />
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Confirm Password</label>
            <input {...register('confirm_password')} type="password" autoComplete="new-password" placeholder="Re-type your password" className={input} />
            {errors.confirm_password && <p className="text-xs text-red-500 mt-1">{errors.confirm_password.message}</p>}
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
          <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Early Access Notice</p>
          <p className="text-xs text-gray-700 leading-relaxed">
            Housewizrd is currently in early access while we build and improve the platform.
            We help connect homeowners with contractors but do not guarantee contractor work
            quality or outcomes. License and insurance verification reflects an automated check
            as of the date shown on each contractor's profile — please verify independently
            before hiring. Your feedback during this period helps shape what we build next.
          </p>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              {...register('early_access_ack')}
              className="mt-0.5 shrink-0 w-4 h-4 cursor-pointer"
            />
            <span className="text-xs text-gray-700">I understand this platform is in early access</span>
          </label>
          {errors.early_access_ack && (
            <p className="text-xs text-red-600">{errors.early_access_ack.message}</p>
          )}
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer bg-white rounded-2xl shadow-sm p-4">
          <input
            type="checkbox"
            {...register('terms_accepted')}
            className="mt-0.5 shrink-0 w-4 h-4 cursor-pointer"
          />
          <span className="text-sm text-gray-700 leading-relaxed">
            I have read and agree to the Housewizrd{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
              Privacy Policy
            </a>
            {' '}and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
              Terms of Service
            </a>
          </span>
        </label>
        {errors.terms_accepted && (
          <p className="text-xs text-red-600 -mt-2 px-1">{errors.terms_accepted.message}</p>
        )}

        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-100">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || termsAccepted !== true}
          className={`w-full py-4 rounded-2xl text-white font-bold text-base transition-colors ${
            selectedTier === 'verified_partner'
              ? 'bg-blue-800 disabled:bg-blue-300'
              : 'bg-blue-700 disabled:bg-blue-300'
          }`}
        >
          {isSubmitting
            ? (isPaidPlan ? 'Creating account…' : 'Creating account…')
            : isPaidPlan
            ? `Create account & start ${planParam} plan`
            : selectedTier === 'verified_partner'
            ? 'Apply for Verified Partner'
            : 'Create Free Listing'}
        </button>
      </form>
    </div>
  );
}
